import { ChangeDetectionStrategy, Component, effect, input, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { InputNumberModule } from 'primeng/inputnumber';
import { RadioButtonModule } from 'primeng/radiobutton';
import type { ChartOptionsConfig } from '../../data/charts/chart-options/chart-options-storage.service';
import { AUTO_RANGE, rangeConfigFromDraft } from './explorer-chart-config';

/** One axis' range control for the explorer's Display settings: Auto (fit data) or Manual min/max. In-memory only. */
@Component({
  selector: 'cosmos-explorer-axis-range',
  imports: [FormsModule, TranslatePipe, RadioButtonModule, InputNumberModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-2 mb-3">
      <span class="text-sm font-semibold text-surface-700 dark:text-surface-300">{{ title() }}</span>

      <div class="flex items-center gap-2">
        <p-radiobutton [inputId]="idPrefix() + '-auto'" [name]="idPrefix() + '-mode'" value="auto-data"
          [ngModel]="mode()" (ngModelChange)="onMode($event)" />
        <label [for]="idPrefix() + '-auto'" class="text-sm">{{ 'monitoring.data-explorer.display.auto' | translate }}</label>
      </div>
      <div class="flex items-center gap-2">
        <p-radiobutton [inputId]="idPrefix() + '-manual'" [name]="idPrefix() + '-mode'" value="manual"
          [ngModel]="mode()" (ngModelChange)="onMode($event)" />
        <label [for]="idPrefix() + '-manual'" class="text-sm">{{ 'monitoring.data-explorer.display.manual' | translate }}</label>
      </div>

      @if (mode() === 'manual') {
        <div class="flex gap-2">
          <div class="flex flex-col gap-1 flex-1 min-w-0">
            <label class="text-xs text-surface-500 dark:text-surface-400">{{ 'monitoring.data-explorer.display.min' | translate }}</label>
            <p-inputNumber [ngModel]="minDraft()" (ngModelChange)="minDraft.set($event); onRange()" [maxFractionDigits]="6" fluid />
          </div>
          <div class="flex flex-col gap-1 flex-1 min-w-0">
            <label class="text-xs text-surface-500 dark:text-surface-400">{{ 'monitoring.data-explorer.display.max' | translate }}</label>
            <p-inputNumber [ngModel]="maxDraft()" (ngModelChange)="maxDraft.set($event); onRange()" [maxFractionDigits]="6" fluid />
          </div>
        </div>
        @if (minDraft() == null || maxDraft() == null) {
          <span class="text-xs text-surface-500 dark:text-surface-400">{{ 'monitoring.data-explorer.display.incomplete' | translate }}</span>
        }
      }
    </div>
  `,
})
export class ExplorerAxisRangeComponent {
  readonly title = input.required<string>();
  /** Unique per instance (radio ids/names). */
  readonly idPrefix = input.required<string>();
  /** Applied config; when the parent resets it (variable changed) the drafts follow. */
  readonly config = input<ChartOptionsConfig>(AUTO_RANGE);
  readonly configChange = output<ChartOptionsConfig>();

  protected readonly mode = signal<'auto-data' | 'manual'>('auto-data');
  protected readonly minDraft = signal<number | null>(null);
  protected readonly maxDraft = signal<number | null>(null);

  constructor() {
    effect(() => {
      const c = this.config();
      untracked(() => {
        this.mode.set(c.yAxisMode === 'manual' ? 'manual' : 'auto-data');
        this.minDraft.set(c.yMin ?? null);
        this.maxDraft.set(c.yMax ?? null);
      });
    });
  }

  protected onMode(mode: 'auto-data' | 'manual'): void {
    this.mode.set(mode);
    this.onRange();
  }

  protected onRange(): void {
    const next = rangeConfigFromDraft(this.mode(), this.minDraft(), this.maxDraft());
    if (next) this.configChange.emit(next);
  }
}
